import {
  ColorPicker,
  ColorArea,
  ColorSlider,
  ColorSwatch,
  ColorField,
  ColorSwatchPicker as HeroColorSwatchPicker,
  Label,
  parseColor,
} from '@heroui/react';
import { useTranslation } from 'react-i18next';

/**
 * Role-colour picker built on HeroUI's composable `ColorPicker` (controlled).
 *
 * The trigger shows the current swatch + hex; the popover carries a curated
 * preset row, a saturation/brightness area, a hue slider and a hex field —
 * all synced through the ColorPicker context. Output stays a `#rrggbb`
 * string, the same contract the parent (RoleEditor) already expects, so
 * nothing downstream changes.
 *
 * When `disabled`, we drop the interactive picker and just render a static
 * swatch + hex (React Aria's ColorPicker has no top-level disabled state).
 */

// A few restrained starting points. The color area still accepts any color.
const SWATCHES = [
  '#a9a6a2', '#c89d74', '#b77864', '#b8a66c',
  '#6d9e88', '#6b97a3', '#718bc0', '#947eb2',
];

function safeParse(hex: string) {
  try {
    return parseColor(hex);
  } catch {
    return parseColor('#a9a6a2');
  }
}

export function ColorSwatchPicker(props: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const current = safeParse(props.value);

  if (props.disabled) {
    return (
      <div className="flex items-center gap-2">
        <ColorSwatch color={current} size="lg" />
        <span className="font-mono text-sm uppercase text-muted">{props.value}</span>
      </div>
    );
  }

  return (
    <ColorPicker value={current} onChange={(c) => props.onChange(c.toString('hex'))}>
      <ColorPicker.Trigger className="gap-2">
        <ColorSwatch size="lg" />
        <Label className="font-mono text-sm uppercase">{props.value}</Label>
      </ColorPicker.Trigger>
      <ColorPicker.Popover className="gap-2">
        <HeroColorSwatchPicker className="justify-center pt-1" size="xs">
          {SWATCHES.map((preset) => (
            <HeroColorSwatchPicker.Item key={preset} color={preset}>
              <HeroColorSwatchPicker.Swatch />
            </HeroColorSwatchPicker.Item>
          ))}
        </HeroColorSwatchPicker>
        <ColorArea
          aria-label={t('cmod.color.satBright')}
          className="max-w-full"
          colorSpace="hsb"
          xChannel="saturation"
          yChannel="brightness"
        >
          <ColorArea.Thumb />
        </ColorArea>
        <ColorSlider aria-label={t('cmod.color.hue')} channel="hue" className="gap-1 px-1" colorSpace="hsb">
          <ColorSlider.Track>
            <ColorSlider.Thumb />
          </ColorSlider.Track>
        </ColorSlider>
        <ColorField aria-label={t('cmod.color.hex')}>
          <ColorField.Group variant="secondary">
            <ColorField.Prefix>
              <ColorSwatch size="xs" />
            </ColorField.Prefix>
            <ColorField.Input />
          </ColorField.Group>
        </ColorField>
      </ColorPicker.Popover>
    </ColorPicker>
  );
}
